"use client";

import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/design-system/input";
import {
  type ApplyFormErrorKey,
  type ApplyFormState,
  looksLikeHttpUrl,
  MAX_PORTFOLIO_URL_LENGTH,
  MAX_SHORT_TEXT_LENGTH,
} from "./applyForm";
import { Field } from "./applyFormPrimitives";
import { PortfolioLinkPreview } from "./PortfolioLinkPreview";
import { SocialProfilePreview } from "./SocialProfilePreview";

export function ApplyWorkStep({
  form,
  errorFor,
  onChange,
}: {
  form: ApplyFormState;
  errorFor: (key: ApplyFormErrorKey) => string | undefined;
  onChange: <K extends keyof ApplyFormState>(
    key: K,
    value: ApplyFormState[K],
  ) => void;
}) {
  const t = useTranslations("apply");
  return (
    <div className="flex flex-col gap-nf-ds-5">
      <div className="flex flex-col gap-nf-ds-2">
        <Field
          label={t("fields.portfolio.label")}
          // Gives way to the link's preview once there is a link to preview,
          // as it does to an error: it has been read by then, and left in
          // place it would sit between the input and the card it produced.
          hint={
            looksLikeHttpUrl(form.portfolioUrl)
              ? undefined
              : t("fields.portfolio.hint")
          }
          error={errorFor("portfolioUrl")}
        >
          <Input
            type="url"
            inputMode="url"
            value={form.portfolioUrl}
            onChange={(e) => onChange("portfolioUrl", e.target.value)}
            maxLength={MAX_PORTFOLIO_URL_LENGTH}
            placeholder={t("fields.portfolio.placeholder")}
          />
        </Field>
        <PortfolioLinkPreview url={form.portfolioUrl} />
      </div>

      <div className="grid gap-nf-ds-5 sm:grid-cols-2">
        <div className="flex flex-col gap-nf-ds-2">
          <Field label={t("fields.tiktok.label")}>
            <Input
              value={form.tiktokHandle}
              onChange={(e) => onChange("tiktokHandle", e.target.value)}
              maxLength={MAX_SHORT_TEXT_LENGTH}
              placeholder={t("fields.handlePlaceholder")}
            />
          </Field>
          <SocialProfilePreview platform="tiktok" handle={form.tiktokHandle} />
        </div>

        <div className="flex flex-col gap-nf-ds-2">
          <Field label={t("fields.instagram.label")}>
            <Input
              value={form.instagramHandle}
              onChange={(e) => onChange("instagramHandle", e.target.value)}
              maxLength={MAX_SHORT_TEXT_LENGTH}
              placeholder={t("fields.handlePlaceholder")}
            />
          </Field>
          <SocialProfilePreview
            platform="instagram"
            handle={form.instagramHandle}
          />
        </div>
      </div>

      {/* Said here rather than enforced: whether a handle leads to a real
          profile is the server's call at submit (autoRejectReason in the
          backend), and an application whose only handle doesn't is stored
          rejected. This is the applicant's warning before they sign. */}
      <p className="rounded-nf-ds-md bg-nf-ds-tone-warn-bg p-nf-ds-3 font-nf-ds-sans text-nf-ds-hint text-nf-ds-tone-warn-fg">
        {t("fields.socialsRule")}
      </p>
    </div>
  );
}
